import { retryUntil } from '../util/retry';
import Swiper from 'swiper';

type ElementWithSwiper = Element & { swiper: Swiper };

interface SlidesToShow {
  mobile: number;
  tablet: number;
  desktop: number;
}

export function initSlidesScripts(): void {
  // get all the sliders present
  const sliderWrappers = document.querySelectorAll('.slider-requires-scripts');

  if (!sliderWrappers) {
    return;
  }

  sliderWrappers.forEach((sliderWrapper) => {
    configureSlider(sliderWrapper);
  });
}

function configureSlider(sliderWrapper: Element) {
  retryUntil(
    () => !!getSwiperInstanceFromWrapper(sliderWrapper),
    () => {
      const swiper = getSwiperInstanceFromWrapper(sliderWrapper);
      const slider = sliderWrapper.querySelector('.elementor-main-swiper') as ElementWithSwiper;

      // should not happen, is to appease the ts compiler
      if (!swiper || !slider) {
        return;
      }

      setHandlePauseButton(slider);
      swiper.on('slideChange', setAriaHidden);
      setAriaHidden(swiper);

      if (sliderWrapper.classList.contains('slider-has-responsive-slides')) {
        // get the SlidesToShow data from data-attributes
        const slidesToShow: SlidesToShow = {
          mobile: parseFloat(parseFloat(sliderWrapper.getAttribute('data-slides-mobile') || '1').toFixed(1)),
          tablet: parseFloat(parseFloat(sliderWrapper.getAttribute('data-slides-tablet') || '2').toFixed(1)),
          desktop: parseFloat(parseFloat(sliderWrapper.getAttribute('data-slides-desktop') || '3').toFixed(1)),
        };

        const spaceBetween = parseInt(sliderWrapper.getAttribute('data-space-between') || '10', 10);

        slider.classList.remove('hidden');
        window.addEventListener('resize', function () {
          handleResponsiveSlides(swiper, spaceBetween, slidesToShow);
        });
        handleResponsiveSlides(swiper, spaceBetween, slidesToShow);
      }

      if (sliderWrapper.classList.contains('slider-has-grouped-navigation')) {
        addWrappingDivToNavigation(slider);
      }
    }
  );
}

function handleResponsiveSlides(swiper: Swiper, spaceBetween: number, slidesToShow: SlidesToShow) {
  let currentDisplaySlides = slidesToShow.desktop;
  const breakpoints = {
    desktop: '(min-width: 1025px)',
    tablet: '(min-width: 768px) and (max-width: 1024px)',
    mobile: '(max-width: 767px)',
  };

  if (window.matchMedia(breakpoints.desktop).matches) {
    currentDisplaySlides = slidesToShow.desktop;
  } else if (window.matchMedia(breakpoints.tablet).matches) {
    currentDisplaySlides = slidesToShow.tablet;
  } else if (window.matchMedia(breakpoints.mobile).matches) {
    currentDisplaySlides = slidesToShow.mobile;
  }

  swiper.params.spaceBetween = spaceBetween;
  swiper.params.slidesPerView = currentDisplaySlides;
  swiper.update();
}

function getSwiperInstanceFromWrapper(sliderWrapper: Element) {
  const mainSwiper = sliderWrapper.querySelector('.elementor-main-swiper');

  if (!mainSwiper) {
    return;
  }

  return (mainSwiper as ElementWithSwiper | null)?.swiper;
}

function setAriaHidden(swiper: Swiper) {
  const slides = swiper.slides;
  const slidesPerView = swiper.params.slidesPerView as number;

  slides.forEach((slide, index) => {
    let isVisible: boolean;

    // this takes into consideration if more than half of a slide is shown,
    // according to guidelines, this is when a slide is considered shown
    const isCentered = swiper.params.centeredSlides;
    const fractionalSlide = slidesPerView % 1 >= 0.5;

    if (isCentered) {
      isVisible = fractionalSlide
        ? index >= swiper.activeIndex - 1 && index < swiper.activeIndex + slidesPerView - 1
        : index >= swiper.activeIndex && index < swiper.activeIndex + slidesPerView;
    } else {
      isVisible = fractionalSlide
        ? index >= swiper.activeIndex && index < swiper.activeIndex + Math.ceil(slidesPerView)
        : index >= swiper.activeIndex && index < swiper.activeIndex + slidesPerView;
    }

    if (!isVisible) {
      slide.setAttribute('aria-hidden', 'true');
      slide.setAttribute('tabindex', '-1');

      slide.querySelectorAll('a, button').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
    } else {
      slide.removeAttribute('aria-hidden');
      slide.removeAttribute('tabindex');

      slide.querySelectorAll('a, button').forEach((element) => {
        element.removeAttribute('tabindex');
      });
    }
  });
}

function setHandlePauseButton(slider: ElementWithSwiper) {
  const pauseButton = slider.querySelector('.slider-pause') as HTMLElement;
  const playButton = slider.querySelector('.slider-play') as HTMLElement;

  if (!pauseButton || !playButton) {
    return;
  }

  pauseButton.addEventListener('click', () => {
    slider.swiper.autoplay.stop();
    pauseButton.style.display = 'none';
    playButton.style.display = 'inline-block';

    playButton.focus();
  });

  playButton.addEventListener('click', () => {
    slider.swiper.autoplay.start();
    playButton.style.display = 'none';
    pauseButton.style.display = 'inline-block';

    pauseButton.focus();
  });
}

function addWrappingDivToNavigation(slider: ElementWithSwiper) {
  const currentContainer = slider.querySelector('.swiper-pagination');

  if (!currentContainer) {
    return;
  }

  // add a wrapper div, which allows us to assign styles to the dot navigation group
  const wrapperDiv = document.createElement('div');
  wrapperDiv.classList.add('pagination-navigation-group');

  // get all the children from currentContainer and assign them to wrapperDiv, after add it to the DOM
  while (currentContainer.firstChild) {
    wrapperDiv.appendChild(currentContainer.firstChild);
  }

  currentContainer.appendChild(wrapperDiv);
}
